/**
 * Standalone, full-page rendering of one RSI(2) Oversold Pullback idea's
 * chart -- opened via the "Open in new tab" button on `Rsi2PullbackPage.tsx`'s
 * `ChartDialog` (see `rsi2ChartHelpers.tsx`'s `rsi2ChartStandaloneUrl`), so
 * the user can see the candlestick chart, RSI(2) panel, and KPI/outcome
 * details at full browser-window size instead of squeezed into a modal.
 *
 * Deliberately registered in `App.tsx` WITHOUT `MainLayout` (no sidebar/top
 * nav) -- the point of opening this in a new tab is a maximized, uncluttered
 * view, not a second copy of the full app shell. Still behind
 * `ProtectedRoute`/the INVESTMENT_IDEAS module gate like the rest of the
 * RSI(2) Oversold Pullback feature.
 *
 * Reads `symbol`/`signalDate` from the URL's query string (not route params)
 * to match `rsi2ChartStandaloneUrl`'s `URLSearchParams` construction.
 */
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import { Alert, Box, Link, Paper, Typography } from "@mui/material";
import Rsi2ChartPanel from "./rsi2-pullback/Rsi2ChartPanel";

export function Rsi2ChartStandalonePage() {
  const [searchParams] = useSearchParams();
  const symbol = searchParams.get("symbol");
  const signalDate = searchParams.get("signalDate");

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default", p: { xs: 2, md: 4 } }}>
      <Box sx={{ maxWidth: 1100, mx: "auto" }}>
        <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            TekInvest — RSI(2) Oversold Pullback
          </Typography>
          <Link component={RouterLink} to="/investing-idea?tab=market-opportunities&view=rsi2-pullback" variant="body2">
            Back to Investing Idea
          </Link>
        </Box>

        {!symbol || !signalDate ? (
          <Alert severity="error">
            Missing symbol/signal date in the URL -- open this chart via the "Open in new tab" button on an idea's
            chart dialog rather than visiting this link directly.
          </Alert>
        ) : (
          <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, borderRadius: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
              {symbol} — RSI(2) Oversold Pullback
            </Typography>
            <Rsi2ChartPanel symbol={symbol} signalDate={signalDate} />
          </Paper>
        )}
      </Box>
    </Box>
  );
}

export default Rsi2ChartStandalonePage;
